import { z } from "zod";
import { InvoiceStatus } from "@common/types/invoice";
import { accountOverridesRequestSchema } from "../accounting/account-override.schema";

const createInvoiceLineItemSchema = z.object({
    productOrService: z.string().max(255).nullable().optional(),
    catalogItemId: z.string().uuid().nullable().optional(),
    catalogItemPriceId: z.string().uuid().nullable().optional(),
    description: z.string().max(1000).nullable(),
    amount: z.number().finite(),
    quantityDecimal: z.number().finite().nullable().optional(),
    unitAmount: z.number().finite().nullable().optional(),
    accountOverrides: accountOverridesRequestSchema.optional(),
    accountsReceivableOverrides: accountOverridesRequestSchema.optional(),
    // Transitional legacy fields. New callers should use the
    // per-ledger override arrays; these resolve to the entity's
    // primary accrual ledger server-side.
    accountId: z.string().uuid().nullable().optional(),
    accountsReceivableId: z.string().uuid().nullable().optional(),
    classIds: z.array(z.string().uuid()).max(20).optional(),
});

export type CreateInvoiceLineItemBody = z.infer<
    typeof createInvoiceLineItemSchema
>;

export const createInvoiceItemSchema = z.object({
    entityId: z.coerce.number().int(),
    financialAccountId: z.coerce.number().int().optional(),
    invoiceNumber: z.string().max(255).nullable(),
    counterpartyId: z.string().uuid().nullable().optional(),
    description: z.string().max(1000).nullable().optional(),
    due: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date().optional(),
    status: z.nativeEnum(InvoiceStatus),
    memo: z.string().max(5000).nullable().optional(),
    currency: z
        .string()
        .regex(/^[a-zA-Z]{3}$/)
        .transform((v) => v.toLowerCase())
        .default("usd"),
    lines: z.array(createInvoiceLineItemSchema).min(1).max(100),
    tax: z.number().finite().nonnegative().optional(),
    discount: z.number().finite().nonnegative().optional(),
    deposit: z.number().finite().nonnegative().optional(),
});

export type CreateInvoiceItem = z.infer<typeof createInvoiceItemSchema>;

export const createInvoicesBodySchema = z.object({
    invoices: z.array(createInvoiceItemSchema).min(1).max(50),
});

export type CreateInvoicesBody = z.infer<typeof createInvoicesBodySchema>;
